import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

type ResourcePanelView = 'new' | 'list_requests' | 'air_ambulance';

interface ResourceRequestsState {
  selectedRequest: string | null;
  isResourcePanelOpen: boolean;
  resourcePanelView: ResourcePanelView;
  previousView: ResourcePanelView | null;
  seenTransportIds: string[];
}

const initialState: ResourceRequestsState = {
  selectedRequest: null,
  isResourcePanelOpen: false,
  resourcePanelView: 'list_requests',
  previousView: null,
  seenTransportIds: [],
};

/**
 * Manages the UI state for the right-hand resource requests panel. See
 * etlIntegrationsApi & hooks for the actual resource request data (e.g. GMR/Air
 * Ambulance transports) are cached under the api key in redux.
 */
const resourceRequestsSlice = createSlice({
  name: 'resourceRequests',
  initialState,
  reducers: {
    setSelectedRequest: (state, action: PayloadAction<string | null>) => {
      state.selectedRequest = action.payload;
    },
    openResourcePanel: state => {
      state.isResourcePanelOpen = true;
      state.resourcePanelView = 'list_requests';
      state.previousView = null;
    },
    closeResourcePanel: state => {
      state.isResourcePanelOpen = false;
      state.selectedRequest = null;
      state.resourcePanelView = 'list_requests';
    },
    setResourcePanelView: (state, action: PayloadAction<ResourcePanelView>) => {
      state.previousView = state.resourcePanelView;
      state.resourcePanelView = action.payload;
    },
    markTransportSeen: (state, action: PayloadAction<string>) => {
      if (!state.seenTransportIds.includes(action.payload)) {
        state.seenTransportIds.push(action.payload);
      }
    },
    initializeSeenTransports: (state, action: PayloadAction<string[]>) => {
      const existing = new Set(state.seenTransportIds);
      for (const id of action.payload) {
        if (!existing.has(id)) {
          state.seenTransportIds.push(id);
        }
      }
    },
    toggleResourcePanel: state => {
      state.isResourcePanelOpen = !state.isResourcePanelOpen;
    },
  },
  selectors: {
    selectSelectedRequestId: state => state.selectedRequest,
    selectIsResourcePanelOpen: state => state.isResourcePanelOpen,
    selectResourcePanelView: state => state.resourcePanelView,
    selectPreviousView: state => state.previousView,
    selectSeenTransportIds: state => state.seenTransportIds,
  },
});

export const {
  setSelectedRequest,
  openResourcePanel,
  closeResourcePanel,
  setResourcePanelView,
  toggleResourcePanel,
  markTransportSeen,
  initializeSeenTransports,
} = resourceRequestsSlice.actions;

const selectors = resourceRequestsSlice.selectors;

export const useSelectedRequestId = () =>
  useSelector(selectors.selectSelectedRequestId);
export const useIsResourcePanelOpen = () =>
  useSelector(selectors.selectIsResourcePanelOpen);
export const useResourcePanelView = () =>
  useSelector(selectors.selectResourcePanelView);
export const usePreviousView = () => useSelector(selectors.selectPreviousView);
export const useSeenTransportIds = () =>
  useSelector(selectors.selectSeenTransportIds);

export type { ResourcePanelView };
export const resourceRequests = resourceRequestsSlice.reducer;
